import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, RefreshCw, MessageSquareWarning } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Sentry, isSentryReady } from "@/lib/sentry";


interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  reported: boolean;
}

async function reportError(error: Error, errorInfo: ErrorInfo | null, route?: string) {
  if (isSentryReady()) {
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo?.componentStack ?? undefined } },
      tags: { route: route ?? window.location.pathname },
    });
  }
  try {
    const user = await supabase.auth.getUser().then((r) => r.data.user).catch(() => null);
    await supabase.functions.invoke("log-app-error", {
      body: {
        source: "frontend",
        severity: "error",
        route: route ?? window.location.pathname,
        message: error.message || "Unknown React error",
        stack: error.stack || errorInfo?.componentStack,
        user_id: user?.id,
        user_email: user?.email,
        metadata: {
          componentStack: errorInfo?.componentStack,
          userAgent: navigator.userAgent,
          href: window.location.href,
        },
      },
    });
  } catch {
    // Never let error reporting break the fallback UI.
  }
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, reported: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, reported: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    reportError(error, errorInfo);
    this.setState({ reported: true });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <DefaultErrorFallback error={this.state.error} onReset={() => this.setState({ hasError: false, error: null, reported: false })} />;
    }
    return this.props.children;
  }
}

function DefaultErrorFallback({ error, onReset }: { error: Error | null; onReset: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-lg shadow-lg border-destructive/20">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-3 font-display text-2xl text-foreground">
            <AlertTriangle className="h-7 w-7 text-destructive" />
            Something went wrong
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground font-body">
            We hit an unexpected issue loading this page. Our team has been notified automatically.
          </p>
          {error?.message && (
            <div className="rounded-md bg-muted p-3 font-mono text-xs text-muted-foreground break-words">
              {error.message}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button onClick={onReset} variant="default" className="gap-2 rounded-full">
              <RefreshCw className="h-4 w-4" />
              Try again
            </Button>
            <Button
              variant="outline"
              className="gap-2 rounded-full"
              onClick={() => window.location.href = "/"}
            >
              <MessageSquareWarning className="h-4 w-4" />
              Go home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
